import { useEffect } from "react";

import { skipToken } from "@tanstack/react-query";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { stylesRouter } from "@v2/trpc/routes/styles";
import { useSearchParams } from "react-router-dom";
import { normalizeColorToHex } from "replo-utils/lib/colors";
import { fiberSelectedPayloadSchema } from "schemas/fileModifiers";

import {
  useModifierQueueActions,
  useWaitingForCleanup,
} from "../store/ModifierQueueStoreContext";
import {
  useDisplayMode,
  useModifiersStoreActions,
} from "../store/ModifiersStoreContext";
import { buildStyleOverrides } from "../utils/buildStyleOverrides";
import { useInlineContentEditor } from "./useInlineContentEditor";
import { useModifiers } from "./useModifiers";

// NOTE (Kevin, 2025-09-27): Keep in sync with
// apps/replo-nextjs-scaffold/editor-scripts/scripts/notify-parent-pointerdown.ts
const CANVAS_POINTER_DOWN_MESSAGE = "replo-editor:canvas-pointerdown";
const CANVAS_KEYDOWN_MESSAGE = "replo-editor:canvas-keydown";

interface IframeMessageRouterDeps {
  showControls: boolean;
  hoverRef: React.RefObject<HTMLDivElement>;
  latestRectRef: React.MutableRefObject<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>;
  groupedElementRectsRef?: React.MutableRefObject<
    { top: number; left: number; width: number; height: number }[]
  >;
}

/**
 * Centralized router for all iframe-to-parent window messages.
 * This consolidates all message handling in one place to avoid scattered side effects.
 */
export function useIframeMessageRouter({
  showControls,
  hoverRef,
  latestRectRef,
  groupedElementRectsRef,
}: IframeMessageRouterDeps) {
  const { performCleanup } = useModifiers();
  const waitingForCleanup = useWaitingForCleanup();
  const displayMode = useDisplayMode();
  const modifierActions = useModifiersStoreActions();
  const queueActions = useModifierQueueActions();
  const { updateText } = useInlineContentEditor();

  const projectId = useCurrentProjectId();
  const [searchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");
  const { data: styleLibraryData } = stylesRouter.getStyleLibrary.useQuery(
    projectId ? { projectId: projectId } : skipToken,
  );

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const type = event.data?.type;

      // Handle canvas pointer events (always active, regardless of display mode)
      if (type === CANVAS_POINTER_DOWN_MESSAGE) {
        const pointerType =
          typeof event.data.pointerType === "string"
            ? event.data.pointerType
            : "mouse";
        const eventTarget: EventTarget = document.body ?? document;

        // NOTE (Kevin, 2025-09-27): We need to dispatch a synthetic pointerdown event so Radix DismissableLayer observes it and dismisses popovers.
        const syntheticEvent =
          typeof PointerEvent === "function"
            ? new PointerEvent("pointerdown", {
                bubbles: true,
                cancelable: true,
                pointerType,
              })
            : new MouseEvent("mousedown", {
                bubbles: true,
                cancelable: true,
              });

        eventTarget.dispatchEvent(syntheticEvent);
        return;
      }

      if (type === CANVAS_KEYDOWN_MESSAGE) {
        const eventTarget: EventTarget = document.body ?? document;

        // NOTE (Kevin, 2025-09-29): Dispatch synthetic keyboard event to propagate keyboard shortcuts from iframe to parent.
        const syntheticKeyEvent = new KeyboardEvent("keydown", {
          bubbles: true,
          cancelable: true,
          key: event.data.key,
          code: event.data.code,
          metaKey: Boolean(event.data.metaKey),
          ctrlKey: Boolean(event.data.ctrlKey),
          shiftKey: Boolean(event.data.shiftKey),
          altKey: Boolean(event.data.altKey),
          repeat: Boolean(event.data.repeat),
        });

        eventTarget.dispatchEvent(syntheticKeyEvent);
        return;
      }

      // NOTE (Nick, 2025-12-04): Handle iframe ready handshake — clear stale selection
      // and rects from the previous iframe session, then mark the iframe as loaded.
      if (type === "IFRAME_READY") {
        modifierActions.setSelectedElementDesignations(null);
        modifierActions.setComputedElementDimensions(null);
        modifierActions.setIframeLoaded(true);
        latestRectRef.current = null;
        if (groupedElementRectsRef) {
          groupedElementRectsRef.current = [];
        }
        return;
      }

      // Handle Fast Refresh completion
      if (type === "FAST_REFRESH_COMPLETE") {
        // NOTE (Kevin, 2025-12-22, REPL-21177): waitingForCleanup is only true when there's a
        // processed request, so we can use it directly without tracking lastProcessedRequest.
        if (waitingForCleanup) {
          // NOTE (Kurt, 2025-11-21): Wait 2 seconds for Tailwind classes to be applied to the DOM
          // before removing optimistic inline styles. This prevents flashing to stale values. Issue
          // here is that there is no way to know when tailwind classes are applied to the DOM after
          // HMR completes, the FAST_REFRESH_COMPLETE message is sent right after HMR but apparently
          // that is not enough time for the DOM to be updated with the new tailwind classes.
          setTimeout(() => {
            performCleanup();
          }, 2000);
        }
        return;
      }

      // All other messages require select mode and controls enabled
      if (!showControls || displayMode !== "select") {
        return;
      }

      if (type === "FIBER_SELECTED") {
        const payload = fiberSelectedPayloadSchema.parse(event.data.payload);

        // Note (Nick, 2025-10-29): Transform styleOverrides from nested to flattened format
        const transformedPayload = {
          ...payload,
          selectedElementDesignations: payload.selectedElementDesignations.map(
            (designation) => {
              return designation.type === "default"
                ? {
                    ...designation,
                    element: {
                      ...designation.element,
                      styleOverrides: buildStyleOverrides(
                        designation.element.styleOverrides,
                        styleLibraryData,
                      ),
                    },
                  }
                : designation;
            },
          ),
        };

        modifierActions.setSelectedElementDesignations(transformedPayload);

        // NOTE (Nick, 2025-12-02): Store grouped element rects when element is selected
        if (groupedElementRectsRef && event.data.payload.groupedElementRects) {
          groupedElementRectsRef.current =
            event.data.payload.groupedElementRects;
        } else if (groupedElementRectsRef) {
          groupedElementRectsRef.current = [];
        }
        return;
      }

      if (type === "HOVER_ELEMENT" && hoverRef.current) {
        const rect = event.data.payload.rect;

        if (rect) {
          Object.assign(hoverRef.current.style, {
            top: `${rect.top}px`,
            left: `${rect.left}px`,
            width: `${rect.width}px`,
            height: `${rect.height}px`,
            display: "block",
          });
        } else {
          hoverRef.current.style.display = "none";
        }
        return;
      }

      if (type === "SELECTED_ELEMENT_SCROLL_UPDATE") {
        const rect = event.data.payload.rect;
        if (rect) {
          latestRectRef.current = rect;
          // NOTE (Derek, 2025-01-14, REPL-20840): Store in modifiers store so dimension selectors
          // can show computed pixel values when in Hug/Fill mode
          modifierActions.setComputedElementDimensions(rect);
        }
        return;
      }

      // NOTE (Nick, 2025-12-02): Handle continuous updates to grouped element rects
      if (type === "GROUPED_ELEMENTS_SCROLL_UPDATE") {
        if (groupedElementRectsRef && event.data.payload.groupedRects) {
          groupedElementRectsRef.current = event.data.payload.groupedRects;
        }
        return;
      }

      // NOTE (Kevin, 2025-12-22, REPL-21177): Handle resolved computed styles from iframe.
      // The iframe resolves __COMPUTE__ values and sends them back here to be enqueued.
      if (type === "COMPUTED_STYLES_RESOLVED") {
        if (!projectId || !pageId) {
          return;
        }

        const { stableElementId, resolvedValues } = event.data.payload as {
          stableElementId: string;
          resolvedValues: { property: string; value: string }[];
        };

        // NOTE (Kevin, 2025-12-22, REPL-21177): Currently only border-color uses __COMPUTE__.
        // Inline the mapping directly here instead of a separate function.
        for (const { property, value } of resolvedValues) {
          if (property === "border-color") {
            // NOTE (Kevin, 2025-12-22, REPL-21177): Only enqueue if we successfully normalize to hex.
            // Skip if normalization fails to avoid sending invalid color formats to the server.
            const normalizedColor = normalizeColorToHex(value);
            if (!normalizedColor?.startsWith("#")) {
              console.warn(
                "[useIframeMessageRouter] Failed to normalize color:",
                value,
              );
              return;
            }

            queueActions.enqueue({
              projectId,
              pageId,
              actions: [
                {
                  stableElementId,
                  modifier: {
                    type: "borderColor",
                    value: normalizedColor,
                  },
                },
              ],
              timestamp: Date.now(),
            });
          }
        }
        return;
      }

      if (type === "UPDATE_TEXT") {
        const { innerHTML } = event.data.payload;
        void updateText(innerHTML);
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [
    performCleanup,
    waitingForCleanup,
    displayMode,
    showControls,
    modifierActions,
    queueActions,
    updateText,
    styleLibraryData,
    hoverRef,
    latestRectRef,
    groupedElementRectsRef,
    projectId,
    pageId,
  ]);
}
